import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ImprovementCalculator from "@/components/ImprovementCalculator";
import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Hammer, Ruler, Globe } from "lucide-react";

const POPULAR = [
  "Kitchen Remodel", "Bathroom Remodel", "House Extension", "Loft / Attic Conversion",
  "Roof Replacement", "New Flooring", "Solar Panels", "Driveway / Paving",
];

export default function HomeImprovement() {
  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary">
            <Home className="mr-2 inline h-3.5 w-3.5" /> Home Improvement Costs
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
            What will your renovation cost?
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Instant, local-currency price ranges for kitchens, extensions, lofts, roofs and more — anywhere in the
            world. Pick a project, your country and size for a ballpark in seconds.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <ImprovementCalculator category="home" />

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Globe, title: "Worldwide", desc: "30+ countries, each in local currency and units." },
            { icon: Ruler, title: "Size-aware", desc: "Costs scale with the actual area of your project." },
            { icon: Hammer, title: "Quality tiers", desc: "From economy to luxury finishes, adjusted per market." },
          ].map((f, i) => (
            <div key={i} className="border border-border p-6">
              <f.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">Popular projects</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {POPULAR.map((p) => (
              <span key={p} className="border border-border px-3 py-1.5 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">{p}</span>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border border-border border-l-2 border-l-primary bg-secondary/20 p-6">
          <div>
            <p className="font-heading text-xl font-bold">Building something bigger?</p>
            <p className="text-muted-foreground">Get a full AI scope-of-work with materials and labour for a whole home or building.</p>
          </div>
          <Link to="/register" data-testid="home-improvement-cta">
            <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              Start a Full Estimate <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
